<script setup>
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onBeforeUnmount,
  onMounted,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import ChatListHeader from './ChatListHeader.vue';
import ConversationList from './ConversationList.vue';
import QuickConversationFilters from './QuickConversationFilters.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import { useAlert } from 'dashboard/composables';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
  compact: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);

// Mi Marciana works from one shared customer-service queue. Individual
// assignment tabs are intentionally disabled: every agent sees the same list.
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const activeQuickFilter = ref('all');
const activeBrandFilter = computed(() => {
  const brand = String(route.query.brand_filter || 'all');
  return ['all', 'mi_marciana', 'planet'].includes(brand) ? brand : 'all';
});
const brandFilterLabel = 'Marca';
const brandFilterItems = [
  { key: 'all', label: 'Ambas' },
  { key: 'mi_marciana', label: 'Mi Marciana' },
  { key: 'planet', label: 'Planet' },
];
const commentsSource = computed(() => {
  const source = String(route.query.comments_source || 'all');
  return ['all', 'instagram', 'facebook'].includes(source) ? source : 'all';
});
const showAdvancedFilters = ref(false);
const showConversationSearch = ref(false);
const conversationSearch = ref('');
const conversationSearchInput = ref(null);
const conversationListComponent = ref(null);
let conversationSearchTimer = null;
const conversationSearchLabels = {
  placeholder: 'Buscar por nombre, usuario, teléfono o mensaje…',
  clear: 'Borrar búsqueda',
  open: 'Buscar conversaciones',
  close: 'Cerrar buscador',
};
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const dashboardFilter = computed(() => String(route.query.mi_filter || 'all'));
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
const globalConversationSearchResults = useMapGetter(
  'conversationSearch/getConversations'
);
const globalConversationSearchUIFlags = useMapGetter(
  'conversationSearch/getUIFlags'
);
const isConversationSearchLoading = computed(
  () =>
    Boolean(conversationSearch.value.trim()) &&
    Boolean(globalConversationSearchUIFlags.value?.isFetching)
);
const remoteConversationSearchIds = computed(
  () =>
    new Set(
      (globalConversationSearchResults.value || []).map(conversation =>
        String(conversation.id)
      )
    )
);
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

const inboxById = computed(
  () =>
    new Map(
      (inboxesList.value || []).map(inboxItem => [
        Number(inboxItem.id),
        inboxItem,
      ])
    )
);

const serverScopedInboxIds = computed(() => {
  if (props.conversationInbox) return [];

  const filter = activeQuickFilter.value;
  if (!['whatsapp', 'instagram', 'messenger'].includes(filter)) return [];

  return (inboxesList.value || [])
    .filter(inboxItem => {
      const descriptor = [
        inboxItem.name,
        inboxItem.channel_type,
        inboxItem.channelType,
        inboxItem.medium,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const commentDescriptor = [
        inboxItem.name,
        inboxItem.integration_kind,
        inboxItem.additional_attributes?.integration_kind,
        inboxItem.channel?.additional_attributes?.integration_kind,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const isCommentInbox =
        commentDescriptor.includes('comentario') ||
        commentDescriptor.includes('instagram_comments') ||
        commentDescriptor.includes('facebook_comments');
      if (isCommentInbox) return false;

      const isPlanet =
        descriptor.includes('planet') ||
        String(inboxItem.id) === '143' ||
        String(inboxItem.id) === '144' ||
        String(inboxItem.id) === '145';
      if (activeBrandFilter.value === 'planet' && !isPlanet) return false;
      if (activeBrandFilter.value === 'mi_marciana' && isPlanet) return false;

      if (filter === 'whatsapp') return descriptor.includes('whatsapp');
      if (filter === 'instagram') return descriptor.includes('instagram');
      return (
        descriptor.includes('facebookpage') ||
        descriptor.includes('messenger')
      );
    })
    .map(inboxItem => Number(inboxItem.id))
    .filter(Boolean);
});

const hasServerScopedInboxFilter = computed(
  () => serverScopedInboxIds.value.length > 0
);

const serverScopedInboxFilters = computed(() => [
  {
    attributeKey: 'inbox_id',
    attributeModel: 'standard',
    filterOperator: 'equal_to',
    queryOperator: 'and',
    values: serverScopedInboxIds.value,
  },
]);

const isCommentsInbox = inboxId => {
  const inboxItem = inboxById.value.get(Number(inboxId));
  if (!inboxItem) return false;

  const descriptor = [
    inboxItem.name,
    inboxItem.integration_kind,
    inboxItem.additional_attributes?.integration_kind,
    inboxItem.channel?.additional_attributes?.integration_kind,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    descriptor.includes('comentario') ||
    descriptor.includes('instagram_comments') ||
    descriptor.includes('facebook_comments')
  );
};

const {
  selectedConversations,
  selectedInboxes,
  selectConversation: selectConversationBase,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const getContact = useMapGetter('contacts/getContact');
const folderContactId = useMapGetter('customViews/getActiveFolderContactId');

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  )
    .filter(({ key }) => key === wootConstants.ASSIGNEE_TYPE.ALL)
    .map(({ key, count: countKey }) => ({
      key,
      name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
      count: conversationStats.value[countKey] || 0,
    }));
});

const showAssigneeInConversationCard = computed(() => {
  // La bandeja de Mi Marciana es compartida. La persona que responde se
  // conserva en cada mensaje, pero no mostramos una asignación de la
  // conversación porque induce a pensar que pertenece a un único agente.
  return false;
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value ||
    hasServerScopedInboxFilter.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  const dashboardLabels =
    dashboardFilter.value === 'shipping_incidents'
      ? ['incidencia-envio']
      : undefined;
  return {
    // Al entrar desde Canales, cada buzón conserva su contenido propio.
    // La cola global solo se utiliza en la vista general del dashboard.
    inboxId: props.conversationInbox || undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : dashboardLabels,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    commentsSource:
      ['comments', 'comments_archive'].includes(activeQuickFilter.value) &&
      commentsSource.value !== 'all'
        ? commentsSource.value
        : undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  const dashboardTitles = {
    archived: 'Archivados',
    email: 'Correos electrónicos',
    unanswered: 'Sin responder',
    changes: 'Cambios',
    unread: 'Mensajes sin leer',
    returns: 'Cambios y devoluciones',
    new_customer: 'Cliente nuevo',
    urgent: 'Mensajes urgentes',
    shipping_incidents: 'Problemas de envío',
  };
  if (dashboardTitles[dashboardFilter.value]) {
    return dashboardTitles[dashboardFilter.value];
  }
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(c => !c.meta?.assignee);
  }
  return [...conversations];
}

function lastRealMessageTimestamp(conversation) {
  const messages = conversation.messages || [];
  const messageTimestamps = messages
    .filter(message =>
      [0, 1, 'incoming', 'outgoing'].includes(message.message_type)
    )
    .map(message => Number(message.created_at || 0));

  return Math.max(
    Number(conversation.last_non_activity_message?.created_at || 0),
    Number(conversation.timestamp || 0),
    ...messageTimestamps,
    0
  );
}

function hasRealMessage(conversation) {
  return lastRealMessageTimestamp(conversation) > 0;
}

function lastRealMessage(conversation) {
  const messages = (conversation.messages || []).filter(message =>
    [0, 1, 'incoming', 'outgoing'].includes(message.message_type)
  );
  return [conversation.last_non_activity_message, ...messages]
    .filter(Boolean)
    .sort(
      (a, b) => Number(b.created_at || 0) - Number(a.created_at || 0)
    )[0];
}

function isActuallyUnanswered(conversation) {
  if (!['open', 'pending'].includes(conversation.status)) return false;
  const lastMessage = lastRealMessage(conversation);
  if (!lastMessage) return false;
  return [0, 'incoming'].includes(lastMessage.message_type);
}

function sortByLastMessage(conversations) {
  return [...conversations].sort((a, b) => {
    const messageDiff =
      lastRealMessageTimestamp(b) - lastRealMessageTimestamp(a);
    if (messageDiff !== 0) return messageDiff;

    // Desempate estable: la conversación más reciente queda primero.
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

function matchesQuickFilter(conversation) {
  const filter = activeQuickFilter.value;
  const scopedInboxId = Number(props.conversationInbox) || null;
  if (scopedInboxId && Number(conversation.inbox_id) !== scopedInboxId) {
    return false;
  }
  const inboxItem = inboxById.value.get(Number(conversation.inbox_id)) || {};
  const channelDescriptor = [
    inboxItem.channel_type,
    inboxItem.channelType,
    inboxItem.medium,
    inboxItem.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const integrationKind = String(
    conversation.additional_attributes?.integration_kind ||
      inboxItem.integration_kind ||
      inboxItem.additional_attributes?.integration_kind ||
      inboxItem.channel?.additional_attributes?.integration_kind ||
      ''
  ).toLowerCase();
  const commentInboxIds = new Set(
    (inboxesList.value || [])
      .filter(item => {
        const descriptor = [
          item.name,
          item.integration_kind,
          item.additional_attributes?.integration_kind,
          item.channel?.additional_attributes?.integration_kind,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return (
          descriptor.includes('comentario') ||
          descriptor.includes('instagram_comments') ||
          descriptor.includes('facebook_comments')
        );
      })
      .map(item => Number(item.id))
  );
  const isSocialComment =
    ['instagram_comments', 'facebook_comments'].includes(integrationKind) ||
    commentInboxIds.has(Number(conversation.inbox_id)) ||
    channelDescriptor.includes('comentario');
  const isEmail =
    channelDescriptor.includes('channel::email') ||
    channelDescriptor.includes('correo') ||
    channelDescriptor.includes('email');
  const isCustomerMessagingChannel = [
    'channel::whatsapp',
    'channel::instagram',
    'channel::facebookpage',
  ].some(channelType => channelDescriptor.includes(channelType));

  if (filter === 'comments' || filter === 'comments_archive') {
    return isSocialComment;
  }
  if (filter === 'all') {
    return scopedInboxId ? true : !isSocialComment && !isEmail;
  }
  if (isSocialComment) return false;
  if (filter === 'email') return isEmail;
  if (isEmail) return false;

  if (filter === 'unanswered') {
    return isCustomerMessagingChannel && isActuallyUnanswered(conversation);
  }

  if (filter === 'changes') {
    return (conversation.labels || []).some(labelItem => {
      const labelName =
        typeof labelItem === 'string'
          ? labelItem
          : labelItem.title || labelItem.name || '';
      return labelName.toLowerCase() === 'cambios';
    });
  }

  if (filter === 'whatsapp') return channelDescriptor.includes('whatsapp');
  if (filter === 'instagram') {
    return (
      channelDescriptor.includes('instagram') &&
      !channelDescriptor.includes('comentario')
    );
  }
  if (filter === 'messenger') {
    return (
      channelDescriptor.includes('facebookpage') ||
      channelDescriptor.includes('messenger')
    );
  }
  return true;
}

function conversationBrand(conversation) {
  const inboxItem = inboxById.value.get(Number(conversation.inbox_id)) || {};
  const attributes = conversation.additional_attributes || {};
  const descriptor = [
    inboxItem.name,
    attributes.brand,
    attributes.store,
    attributes.source_brand,
    attributes.page_id,
    attributes.instagram_id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return descriptor.includes('planet') ||
    descriptor.includes('1352381387960434') ||
    descriptor.includes('17841423561810434')
    ? 'planet'
    : 'mi_marciana';
}

function matchesBrandFilter(conversation) {
  return (
    activeBrandFilter.value === 'all' ||
    conversationBrand(conversation) === activeBrandFilter.value
  );
}

function brandFilterButtonClass(brandKey) {
  if (activeBrandFilter.value === brandKey) {
    return brandKey === 'planet'
      ? 'border-[#e79ab8] bg-[#fde7f0] text-[#8b3154]'
      : 'border-n-slate-12 bg-n-slate-12 text-n-slate-1';
  }

  return brandKey === 'planet'
    ? 'border-[#efbfd1] bg-[#fff4f8] text-[#9f4869] hover:bg-[#fde7f0]'
    : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2';
}

function conversationLabelNames(conversation) {
  return (conversation.labels || []).map(labelItem =>
    String(
      typeof labelItem === 'string'
        ? labelItem
        : labelItem.title || labelItem.name || ''
    ).toLocaleLowerCase('es')
  );
}

function matchesDashboardFilter(conversation) {
  const filter = dashboardFilter.value;
  if (!filter || filter === 'all') return true;
  const labelsForConversation = conversationLabelNames(conversation);
  if (filter === 'archived') return conversation.status === 'resolved';
  if (filter === 'unanswered') return isActuallyUnanswered(conversation);
  if (filter === 'changes') {
    return labelsForConversation.includes('cambios');
  }
  if (filter === 'unread') return Number(conversation.unread_count || 0) > 0;
  if (filter === 'returns') {
    return labelsForConversation.some(label =>
      ['cambios', 'cambio', 'devoluciones', 'devolucion'].includes(label)
    );
  }
  if (filter === 'new_customer') {
    return labelsForConversation.some(label =>
      ['cliente nuevo', 'clienta nueva', 'cliente_nuevo'].includes(label)
    );
  }
  if (filter === 'urgent') {
    return (
      conversation.priority === 'urgent' ||
      labelsForConversation.some(label =>
        ['urgente', 'prioridad urgente'].includes(label)
      )
    );
  }
  if (filter === 'shipping_incidents') {
    return labelsForConversation.includes('incidencia-envio');
  }
  return true;
}

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^@/, '')
    .toLowerCase();
}

function matchesConversationSearch(conversation) {
  const query = normalizeSearchText(conversationSearch.value.trim());
  if (!query) return true;
  // El buscador remoto puede encontrar la consulta en cualquier mensaje
  // histórico. No descartamos ese resultado solo porque el último mensaje
  // visible no contenga, por ejemplo, una dirección antigua.
  if (remoteConversationSearchIds.value.has(String(conversation.id))) {
    return true;
  }
  // Once the server has returned identity matches, it is authoritative. This
  // prevents partial local text such as "canina" from appearing for "Nina".
  if (
    !isConversationSearchLoading.value &&
    remoteConversationSearchIds.value.size > 0
  ) {
    return false;
  }
  const sender = conversation.meta?.sender || {};
  const lastMessage =
    conversation.last_non_activity_message ||
    conversation.messages?.[conversation.messages.length - 1] ||
    {};
  const searchable = [
    conversation.id,
    conversation.display_id,
    sender.name,
    sender.available_name,
    sender.identifier,
    sender.phone_number,
    sender.email,
    sender.additional_attributes?.social_instagram_user_name,
    lastMessage.content,
  ]
    .map(normalizeSearchText)
    .join(' ');
  return searchable.includes(query);
}

async function toggleConversationSearch() {
  showConversationSearch.value = !showConversationSearch.value;
  if (!showConversationSearch.value) {
    conversationSearch.value = '';
    return;
  }
  await nextTick();
  conversationSearchInput.value?.focus();
}

async function openConversationSearchFromMobileMenu() {
  showConversationSearch.value = true;
  await nextTick();
  conversationSearchInput.value?.focus();
}

const conversationList = computed(() => {
  let localConversationList = [];

  if (
    !hasAppliedFiltersOrActiveFolders.value &&
    !hasServerScopedInboxFilter.value
  ) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (conversationSearch.value.trim()) {
    // La búsqueda remota amplía los resultados, pero nunca puede sustituir
    // las conversaciones ya visibles: el endpoint puede no localizar un
    // contacto por su nombre aunque la tarjeta esté cargada en la bandeja.
    const merged = [
      ...localConversationList,
      ...(globalConversationSearchResults.value || []),
    ];
    localConversationList = Array.from(
      new Map(
        merged.map(conversation => [conversation.id, conversation])
      ).values()
    );
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  localConversationList = localConversationList.filter(matchesQuickFilter);
  localConversationList = localConversationList.filter(matchesBrandFilter);
  localConversationList = localConversationList.filter(matchesDashboardFilter);
  if (['comments', 'comments_archive'].includes(activeQuickFilter.value)) {
    const commentsStatus =
      activeQuickFilter.value === 'comments_archive'
        ? wootConstants.STATUS_TYPE.RESOLVED
        : wootConstants.STATUS_TYPE.OPEN;
    localConversationList = localConversationList.filter(
      conversation => conversation.status === commentsStatus
    );
  }
  localConversationList = localConversationList.filter(
    matchesConversationSearch
  );
  // Los conectores pueden anunciar chats cuyo historial todavía no contiene
  // ningún mensaje utilizable. Se conservan para poder reintentar la descarga,
  // pero no ocupan espacio ni alteran el orden de la bandeja.
  localConversationList = localConversationList.filter(hasRealMessage);

  // Mi Marciana debe conservar siempre el orden cronológico del último
  // mensaje. Los cambios de leído/no leído no pueden mover conversaciones.
  return sortByLastMessage(localConversationList);
});

const showEndOfListMessage = computed(() => {
  return !!(
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const viewingCommentsInbox =
    props.conversationInbox &&
    isCommentsInbox(props.conversationInbox);
  const isCommentsArchive = route.query.comments_view === 'archive';

  // Public comments have an operational queue and a separate searchable
  // archive. Other inboxes keep their complete history visible as before.
  activeStatus.value = wootConstants.STATUS_TYPE.ALL;
  if (viewingCommentsInbox) {
    activeStatus.value = isCommentsArchive
      ? wootConstants.STATUS_TYPE.RESOLVED
      : wootConstants.STATUS_TYPE.OPEN;
  }
  // Always reopen the queue with the newest real message first. A previously
  // saved browser preference must not scramble the operational inbox.
  activeSortBy.value = wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    contacts: [getContact.value(folderContactId.value)],
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  if (hasServerScopedInboxFilter.value) {
    fetchFilteredConversations(serverScopedInboxFilters.value);
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (hasServerScopedInboxFilter.value) {
    fetchFilteredConversations(serverScopedInboxFilters.value);
  } else if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function quickFilterForInbox(inboxId) {
  const inboxItem = inboxById.value.get(Number(inboxId));
  if (!inboxItem) return 'all';

  if (isCommentsInbox(inboxId)) return 'comments';

  const descriptor = [
    inboxItem.name,
    inboxItem.channel_type,
    inboxItem.channelType,
    inboxItem.medium,
    inboxItem.integration_kind,
    inboxItem.additional_attributes?.integration_kind,
    inboxItem.channel?.additional_attributes?.integration_kind,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (descriptor.includes('whatsapp')) return 'whatsapp';
  if (
    descriptor.includes('channel::email') ||
    descriptor.includes('correo') ||
    descriptor.includes('email')
  ) {
    return 'email';
  }
  if (descriptor.includes('instagram')) return 'instagram';
  if (descriptor.includes('facebookpage') || descriptor.includes('messenger')) {
    return 'messenger';
  }
  return 'all';
}

async function onQuickFilterChange(value) {
  resetBulkActions();
  // Una búsqueda anterior no debe dejar la nueva bandeja aparentemente
  // vacía o bloqueada. Cada cambio de canal vuelve a mostrar su lista.
  conversationSearch.value = '';
  showConversationSearch.value = false;
  activeQuickFilter.value = value;

  // All summary buttons are aggregate views. The underlying inbox remains on
  // every conversation, so replies still leave through the correct account.
  if (
    [
      'all',
      'whatsapp',
      'instagram',
      'messenger',
      'email',
      'comments',
      'comments_archive',
      'unanswered',
      'changes',
    ].includes(value)
  ) {
    if (value === 'comments' || value === 'comments_archive') {
      activeStatus.value =
        value === 'comments_archive'
          ? wootConstants.STATUS_TYPE.RESOLVED
          : wootConstants.STATUS_TYPE.OPEN;
    } else {
      activeStatus.value = wootConstants.STATUS_TYPE.ALL;
    }
    const targetRoute = {
      name: 'conversation_home',
      params: { accountId: route.params.accountId },
      query: {
        mi_filter: value,
        brand_filter: activeBrandFilter.value,
        ...(value === 'comments_archive' ? { comments_view: 'archive' } : {}),
      },
    };
    if (props.conversationInbox || route.query.mi_filter !== value) {
      await router.push(targetRoute);
      return;
    }
    resetAndFetchData();
  }
}

async function onBrandFilterChange(value) {
  const brand = ['all', 'mi_marciana', 'planet'].includes(value)
    ? value
    : 'all';
  await router.push({
    name: 'conversation_home',
    params: { accountId: route.params.accountId },
    query: {
      ...route.query,
      mi_filter: activeQuickFilter.value,
      brand_filter: brand,
    },
  });
}

async function onCommentsSourceChange(source) {
  if (commentsSource.value === source) return;
  await router.push({
    name: route.name,
    params: route.params,
    query: {
      ...route.query,
      comments_source: source === 'all' ? undefined : source,
    },
  });
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

function conversationReturnStorageKey(conversationId) {
  return `mm-chatwoot:conversation-return:${currentAccountId.value}:${conversationId}`;
}

function latestConversationReturnStorageKey() {
  return `mm-chatwoot:conversation-return:${currentAccountId.value}:latest`;
}

function rememberConversationListState(conversationId) {
  if (!conversationId) return;
  const listElement = conversationListComponent.value?.conversationListRef;
  const serializedState = JSON.stringify({
    conversationId,
    fullPath: route.fullPath,
    quickFilter: activeQuickFilter.value,
    scrollTop: listElement?.scrollTop || 0,
  });
  window.sessionStorage.setItem(
    conversationReturnStorageKey(conversationId),
    serializedState
  );
  window.sessionStorage.setItem(
    latestConversationReturnStorageKey(),
    serializedState
  );
}

function selectConversation(conversationId, inboxId) {
  rememberConversationListState(conversationId);
  return selectConversationBase(conversationId, inboxId);
}

async function restoreConversationListState() {
  const rawState = window.sessionStorage.getItem(
    latestConversationReturnStorageKey()
  );
  if (!rawState) return;

  try {
    const state = JSON.parse(rawState);
    if (state.fullPath !== route.fullPath) return;
    await nextTick();
    window.requestAnimationFrame(() => {
      const listElement = conversationListComponent.value?.conversationListRef;
      if (listElement) listElement.scrollTop = Number(state.scrollTop) || 0;
    });
  } catch (error) {
    window.sessionStorage.removeItem(latestConversationReturnStorageKey());
  }
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

onMounted(() => {
  window.addEventListener(
    'mi-marciana:open-conversation-search',
    openConversationSearchFromMobileMenu
  );
  const genericQuickFilters = [
    'all',
    'whatsapp',
    'instagram',
    'messenger',
    'email',
    'comments',
    'comments_archive',
    'unanswered',
    'changes',
  ];
  const requestedGenericFilter = String(route.query.mi_filter || 'all');
  const initialQuickFilter = props.conversationInbox
    ? quickFilterForInbox(props.conversationInbox)
    : genericQuickFilters.includes(requestedGenericFilter)
      ? requestedGenericFilter
      : 'all';
  activeQuickFilter.value =
    initialQuickFilter === 'comments' && route.query.comments_view === 'archive'
      ? 'comments_archive'
      : initialQuickFilter;
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

onBeforeUnmount(() => {
  window.removeEventListener(
    'mi-marciana:open-conversation-search',
    openConversationSearchFromMobileMenu
  );
  if (conversationSearchTimer) window.clearTimeout(conversationSearchTimer);
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);
provide('rememberConversationListState', rememberConversationListState);

watch(
  () => props.showConversationList,
  isVisible => {
    if (isVisible) restoreConversationListState();
  }
);

watch(activeTeam, () => resetAndFetchData());

watch(inboxesList, () => {
  if (!props.conversationInbox) {
    // The inbox catalogue is loaded asynchronously. Re-run an aggregate
    // channel query as soon as its real inbox ids are available.
    if (hasServerScopedInboxFilter.value) resetAndFetchData();
    return;
  }
  const quickFilter = quickFilterForInbox(props.conversationInbox);
  activeQuickFilter.value =
    quickFilter === 'comments' && route.query.comments_view === 'archive'
      ? 'comments_archive'
      : quickFilter;
});

watch(conversationSearch, value => {
  if (conversationSearchTimer) window.clearTimeout(conversationSearchTimer);
  const query = value.trim();
  conversationSearchTimer = window.setTimeout(
    () => {
      store.dispatch('conversationSearch/get', { q: query });
    },
    query ? 250 : 0
  );
});

watch(
  computed(() => props.conversationInbox),
  inboxId => {
    const quickFilter = inboxId ? quickFilterForInbox(inboxId) : 'all';
    activeQuickFilter.value =
      quickFilter === 'comments' && route.query.comments_view === 'archive'
        ? 'comments_archive'
        : quickFilter;
    setFiltersFromUISettings();
    resetAndFetchData();
  }
);
watch(
  () => route.query.comments_view,
  () => {
    if (!['comments', 'comments_archive'].includes(activeQuickFilter.value)) {
      return;
    }

    activeQuickFilter.value =
      route.query.comments_view === 'archive' ? 'comments_archive' : 'comments';
    setFiltersFromUISettings();
    resetAndFetchData();
  }
);
watch(
  () => route.query.comments_source,
  () => {
    if (!['comments', 'comments_archive'].includes(activeQuickFilter.value)) {
      return;
    }
    resetAndFetchData();
  }
);
watch(
  () => route.query.mi_filter,
  value => {
    if (props.conversationInbox) return;
    const genericQuickFilters = [
      'all',
      'whatsapp',
      'instagram',
      'messenger',
      'email',
      'comments',
      'comments_archive',
      'unanswered',
      'changes',
    ];
    const nextFilter = genericQuickFilters.includes(String(value || 'all'))
      ? String(value || 'all')
      : 'all';
    if (activeQuickFilter.value === nextFilter) return;
    activeQuickFilter.value = nextFilter;
    resetAndFetchData();
  }
);
watch(
  () => route.query.brand_filter,
  () => {
    if (props.conversationInbox) return;
    resetAndFetchData();
  }
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="relative flex flex-shrink-0 flex-col bg-n-surface-1 transition-[width] duration-300 conversations-list-wrap"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout
        ? 'basis-full'
        : compact
          ? 'w-[clamp(190px,14vw,240px)]'
          : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :is-search-open="showConversationSearch"
      :show-panel-button="route.name === 'conversation_home'"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @toggle-search="toggleConversationSearch"
    />

    <div
      v-if="showConversationSearch"
      class="flex flex-none items-center gap-2 border-b border-n-weak bg-n-surface-1 px-3 py-2"
    >
      <Icon icon="i-lucide-search" class="size-4 flex-none text-n-slate-10" />
      <input
        ref="conversationSearchInput"
        v-model="conversationSearch"
        type="search"
        :placeholder="conversationSearchLabels.placeholder"
        class="h-9 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
      />
      <button
        v-if="conversationSearch"
        type="button"
        class="grid size-8 flex-none place-items-center rounded-full text-n-slate-10 hover:bg-n-alpha-2"
        :aria-label="conversationSearchLabels.clear"
        @click="conversationSearch = ''"
      >
        <Icon icon="i-lucide-x" class="size-4" />
      </button>
    </div>

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <QuickConversationFilters
      v-if="!hasAppliedFiltersOrActiveFolders"
      :model-value="activeQuickFilter"
      @update:model-value="onQuickFilterChange"
    />

    <nav
      v-if="!hasAppliedFiltersOrActiveFolders && !conversationInbox"
      class="flex flex-none gap-1.5 overflow-x-auto border-b border-n-weak bg-n-surface-1 px-3 pb-2"
      :aria-label="brandFilterLabel"
    >
      <button
        v-for="brand in brandFilterItems"
        :key="brand.key"
        type="button"
        class="flex h-8 flex-none items-center rounded-full border px-3 text-xs font-medium transition-colors"
        :class="brandFilterButtonClass(brand.key)"
        :aria-pressed="activeBrandFilter === brand.key"
        @click="onBrandFilterChange(brand.key)"
      >
        {{ brand.label }}
      </button>
    </nav>

    <nav
      v-if="['comments', 'comments_archive'].includes(activeQuickFilter)"
      class="flex flex-none gap-1.5 overflow-x-auto border-b border-n-weak bg-n-surface-1 px-3 pb-2"
      aria-label="Origen de los comentarios"
    >
      <button
        v-for="source in [
          { key: 'all', label: 'Instagram + Facebook', icon: 'i-lucide-layout-list' },
          { key: 'instagram', label: 'Instagram', icon: 'i-ri-instagram-fill' },
          { key: 'facebook', label: 'Facebook', icon: 'i-ri-facebook-circle-fill' },
        ]"
        :key="source.key"
        type="button"
        class="flex h-8 flex-none items-center gap-1.5 rounded-full border px-3 text-xs font-medium"
        :class="
          commentsSource === source.key
            ? 'border-n-slate-12 bg-n-slate-12 text-n-slate-1'
            : 'border-n-weak bg-n-alpha-1 text-n-slate-11'
        "
        @click="onCommentsSourceChange(source.key)"
      >
        <Icon :icon="source.icon" class="size-3.5" />
        {{ source.label }}
      </button>
    </nav>

    <p
      v-if="isConversationSearchLoading && !conversationList.length"
      class="flex items-center justify-center gap-2 p-4 text-n-slate-11"
    >
      <Icon icon="i-lucide-loader-circle" class="size-4 animate-spin" />
      Buscando conversaciones…
    </p>
    <p
      v-else-if="!chatListLoading && !conversationList.length"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      :class="isOnExpandedLayout && 'sm:!w-[24rem] !w-full'"
      @select-all-conversations="toggleSelectAll"
    />
    <ConversationList
      ref="conversationListComponent"
      :conversation-list="conversationList"
      :is-loading="chatListLoading && !conversationList.length"
      :show-end-of-list-message="showEndOfListMessage"
      :label="label"
      :team-id="teamId"
      :folders-id="foldersId"
      :conversation-type="conversationType"
      :show-assignee="showAssigneeInConversationCard"
      :is-on-expanded-layout="isOnExpandedLayout"
      @load-more="loadMoreConversations"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
